import { useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { useSelectorApp } from "../../lib/redux";
import useOwnerAdSpend from "../../hooks/useOwnerAdSpend";
import {
  ERROR_OCCURRED,
  OWNER_AD_SPEND_CUSTOM_PENDING,
  OWNER_AD_SPEND_DESCRIPTION,
  OWNER_AD_SPEND_EMPTY_DESCRIPTION,
  OWNER_AD_SPEND_EMPTY_TITLE,
  OWNER_AD_SPEND_MOCK_BADGE,
  OWNER_AD_SPEND_TITLE,
} from "../../i18n/keysTranslations";
import { AD_SPEND_DATE_PRESET, BORDER_RADIUS } from "../../utils/constants";
import AdSpendAccuracyWarning from "./components/adSpend/AdSpendAccuracyWarning";
import AdSpendFilters from "./components/adSpend/AdSpendFilters";
import AdSpendKpiCards from "./components/adSpend/AdSpendKpiCards";
import AdSpendPlatformBreakdown from "./components/adSpend/AdSpendPlatformBreakdown";
import AdSpendTopAdvertisersTable from "./components/adSpend/AdSpendTopAdvertisersTable";
import AdSpendTopCountriesTable from "./components/adSpend/AdSpendTopCountriesTable";
import AdSpendTrendChart from "./components/adSpend/AdSpendTrendChart";
import { AD_SPEND_CARD_SX } from "./components/adSpend/adSpendDisplay";

const OwnerAdSpendBrief = () => {
  const { t } = useTranslationApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const [preset, setPreset] = useState(AD_SPEND_DATE_PRESET.historic);
  const { control, watch } = useForm({
    defaultValues: { dateRange: { startDateTime: null, endDateTime: null } },
  });
  const customRange = watch("dateRange");
  const {
    isLoading,
    error,
    isEmpty,
    isMock,
    isCustomPending,
    granularity,
    chartData,
    platformShare,
    topAdvertisers,
    topCountries,
    kpis,
    showAccuracyWarning,
    comparisonLabelKey,
  } = useOwnerAdSpend({ preset, customRange });

  const showEmpty = !isLoading && !error && isEmpty;
  const showReport = !error && !showEmpty;
  const showComparison = preset !== AD_SPEND_DATE_PRESET.historic;

  return (
    <ContainerPage hasMarginBottom={false}>
      <Helmet>
        <title>{`${theme?.nameBusiness || ""} - ${t(OWNER_AD_SPEND_TITLE)}`}</title>
      </Helmet>
      <Box sx={{ width: "100%", px: 3, mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <TitlePage sx={{ mb: 0.5 }}>{t(OWNER_AD_SPEND_TITLE)}</TitlePage>
            <Typography variant="body2" color="text.secondary">
              {t(OWNER_AD_SPEND_DESCRIPTION)}
            </Typography>
          </Box>
          {isMock ? (
            <Chip
              size="small"
              color="warning"
              label={t(OWNER_AD_SPEND_MOCK_BADGE)}
            />
          ) : null}
        </Box>
        <Box sx={{ mt: 2 }}>
          <AdSpendFilters
            preset={preset}
            onChangePreset={setPreset}
            control={control}
          />
          {isCustomPending ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t(OWNER_AD_SPEND_CUSTOM_PENDING)}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          px: 3,
          mt: 2,
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {showAccuracyWarning ? (
          <AdSpendAccuracyWarning estimatedSpendUSD={kpis.estimatedSpendUSD} />
        ) : null}

        {error ? (
          <Typography color="error">{t(ERROR_OCCURRED)}</Typography>
        ) : null}

        {showEmpty ? (
          <Box
            sx={{
              ...AD_SPEND_CARD_SX,
              borderRadius: BORDER_RADIUS,
              px: 3,
              py: 6,
              textAlign: "center",
            }}
          >
            <Typography variant="h6">
              {t(OWNER_AD_SPEND_EMPTY_TITLE)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t(OWNER_AD_SPEND_EMPTY_DESCRIPTION)}
            </Typography>
          </Box>
        ) : null}

        {showReport ? (
          <>
            <AdSpendKpiCards
              kpis={kpis}
              chartData={chartData}
              comparisonLabelKey={comparisonLabelKey}
              showComparison={showComparison}
              isLoading={isLoading}
            />
            <AdSpendTrendChart
              chartData={chartData}
              granularity={granularity}
              isLoading={isLoading}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <AdSpendPlatformBreakdown
                  platformShare={platformShare}
                  totalSpendUSD={kpis.totalSpendUSD}
                  showTrend={showComparison}
                  isLoading={isLoading}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <AdSpendTopCountriesTable
                  topCountries={topCountries}
                  isLoading={isLoading}
                />
              </Grid>
            </Grid>
            <AdSpendTopAdvertisersTable
              rows={topAdvertisers}
              isLoading={isLoading}
            />
          </>
        ) : null}
      </Box>
    </ContainerPage>
  );
};

export default OwnerAdSpendBrief;
